import React, { useMemo } from 'react';
import { Button, Skeleton, Empty } from 'antd';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAppointmentSlots } from '../../hooks/useAppointmentSlots';
import type { AppointmentSlot, AppointmentSlotGroup } from 'vl-common/src/schemas/getAppointmentSlots';
import { SliceParams } from 'vl-common/src/hooks/useWeekChunkNavigation';

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;
  min-width: 0;
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  padding-right: 6px;
  margin-bottom: 16px;
  background-color: #ffffff;

  @media (min-width: 768px) {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
`;

const DayHeader = styled.div<{ $hasSlots: boolean }>`
  text-align: center;

  .day-name {
    font-size: 14px;
    font-weight: 400;
    color: #8c8c8c;
  }
  .day-number {
    font-size: 14px;
    font-weight: ${({ $hasSlots }) => ($hasSlots ? 700 : 400)};
    color: ${({ $hasSlots }) => ($hasSlots ? 'var(--productPrimaryDark)' : '#666')};
  }
`;

const SlotsScrollContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  max-height: 480px;
  overflow-y: auto;
  overflow-x: auto;
  padding-right: 4px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 8px;
  }
`;

const DayColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;

const SlotButton = styled(Button)`
  padding: 0 1px;
  width: 100%;
  height: 36px;
  border-radius: 4px;
  font-size: 14px;
  border-color: #d9d9d9;
  color: var(--productPrimaryDark) !important;

  &.ant-btn:hover,
  &.ant-btn:focus,
  &.ant-btn:active {
    border-color: var(--productPrimary) !important;
    color: var(--productPrimaryDark) !important;

    > span {
      color: var(--productPrimaryDark) !important;
    }
  }

  &.ant-btn:focus-visible {
    outline: 2px solid var(--productPrimary);
    outline-offset: 2px;
  }
`;

const NoSlotsForDay = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 4px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const StyledMoreButton = styled(Button)`
  margin-top: 4px;
  font-weight: 500;

  &:hover {
    font-weight: 700;
  }
`;

export type DaySlotGroup = Omit<AppointmentSlotGroup, 'slots'> & {
  dateKey: string;
  slots: AppointmentSlot[];
  _rawGroup?: AppointmentSlotGroup;
};

export type SlotViewGridProps = {
  patientGuid?: string;
  weekOffset: number;
  sliceParams: SliceParams;
  clinSubtype?: string;
  clinSpeccat?: string;
  clinSpecarea?: string;
  apptTypeCode?: string;
  clinGuid?: string;
  onSelectSlot: (slot: AppointmentSlot) => void;
  onShowMoreForDay: (dayGroup: DaySlotGroup) => void;
};

const START_TIME = '08:00';
const END_TIME = '17:00';

const formatDayNumber = (firstSlotTime: string | undefined, dom: DaySlotGroup['dom']) => {
  if (firstSlotTime) return dayjs(firstSlotTime).format('D MMM');
  if (dayjs(dom).isValid()) return dayjs(dom).format('D MMM');

  return String(dom || '');
};

export const SlotViewGrid: React.FC<SlotViewGridProps> = ({
  patientGuid,
  weekOffset,
  sliceParams,
  clinSubtype,
  clinSpeccat,
  clinSpecarea,
  apptTypeCode,
  clinGuid,
  onSelectSlot,
  onShowMoreForDay
}) => {
  const { t } = useTranslation('common');

  const { data, isLoading, isError } = useAppointmentSlots({
    patientGuid,
    weekOffset,
    clinSubtype,
    clinSpeccat,
    clinSpecarea,
    apptTypeCode,
    clinGuid
  });

  const sortedDayGroups: DaySlotGroup[] = useMemo(() => {
    if (!data?.appointment_slots) return [];

    return [...data.appointment_slots]
      .map((group) => {
        const sortedSlots = [...(group.slots || [])].sort(
          (a, b) => dayjs(a.slot_datetime).valueOf() - dayjs(b.slot_datetime).valueOf()
        );

        // Standardize the date key using first slot's timestamp or dom
        const referenceTime = sortedSlots[0]?.slot_datetime || group.dom;
        const dateKey = dayjs(referenceTime).format('YYYY-MM-DD');

        return {
          ...group,
          dateKey,
          slots: sortedSlots,
          _rawGroup: group
        };
      })
      .sort((a, b) => {
        const dateA = a.slots[0]?.slot_datetime || a.dom;
        const dateB = b.slots[0]?.slot_datetime || b.dom;
        return dayjs(dateA).valueOf() - dayjs(dateB).valueOf();
      });
  }, [data?.appointment_slots]);

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  if (isError || !sortedDayGroups.length) {
    return <Empty description={t('No available slots for this week')} />;
  }

  const visibleDayGroups = sortedDayGroups.slice(...sliceParams);

  return (
    <GridContainer>
      <HeaderGrid>
        {visibleDayGroups.map((dayGroup, idx) => {
          const firstSlotTime = dayGroup.slots?.[0]?.slot_datetime;
          const formattedDom = formatDayNumber(firstSlotTime, dayGroup.dom);

          const dayOfWeekName = dayjs(firstSlotTime || dayGroup.dom).isValid()
            ? dayjs(firstSlotTime || dayGroup.dom).format('ddd')
            : String(dayGroup.dow || '');

          return (
            <DayHeader key={`header-${dayGroup.dateKey || idx}`} $hasSlots={dayGroup.slots.length > 0}>
              <div className="day-name">{dayOfWeekName}</div>
              <div className="day-number">{formattedDom}</div>
            </DayHeader>
          );
        })}
      </HeaderGrid>

      <SlotsScrollContainer>
        {visibleDayGroups.map((dayGroup) => {
          const MAX_FALLBACK_SLOTS = 5;

          const inWindowSlots = dayGroup.slots.filter((slot) => {
            const timeStr = dayjs(slot.slot_datetime).format('HH:mm');
            return timeStr >= START_TIME && timeStr <= END_TIME;
          });

          const visibleSlots = inWindowSlots.length > 0 ? inWindowSlots : dayGroup.slots.slice(0, MAX_FALLBACK_SLOTS);

          const hasAnySlots = dayGroup.slots.length > 0;
          const hasMoreSlots = dayGroup.slots.length > visibleSlots.length;

          return (
            <DayColumn key={`day-col-${dayGroup.dateKey}`}>
              {!hasAnySlots && <NoSlotsForDay>{t('No slots available')}</NoSlotsForDay>}

              {visibleSlots.map((slot) => {
                const displayTime = dayjs(slot.slot_datetime).format('HH:mm');
                return (
                  <SlotButton
                    key={slot.slot_datetime}
                    onClick={() => onSelectSlot(slot)}
                    data-testid={`slot-btn-${displayTime}`}
                  >
                    {displayTime}
                  </SlotButton>
                );
              })}

              {hasMoreSlots && (
                <StyledMoreButton
                  data-testid="more-slots-button"
                  type="link"
                  size="small"
                  onClick={() => onShowMoreForDay(dayGroup)}
                >
                  {t('More')}
                </StyledMoreButton>
              )}
            </DayColumn>
          );
        })}
      </SlotsScrollContainer>
    </GridContainer>
  );
};
